import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import { CountryWithStateModel } from "./CountryModels";
import {
  NMIAccountHolderType,
  NMIAccountType,
  NMIProcessorTypes,
} from "../enums";
import {
  IAPICreateMerchantAccountModel,
  IAPICreateNMIMerchantModel,
  INMIAccountInfo,
  INMIProcessorFieldsRequest,
  INMIProcessorRequest,
} from "../interfaces";
import {
  ELAVON_EMV_CONFIG,
  FIRST_DATA_NASHVILLE_CONFIG,
  FIRST_DATA_NASHVILLE_NORTH_CONFIG,
  FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG,
  GLOBAL_PAYMENTS_EAST_EMV_CONFIG,
  getFieldRegExp,
} from "../config/ProcessorServiceConfigs";

export class NMIAccountInfoModel {
  checkAccount: string;
  checkAba: string;
  accountHolderType: keyof typeof NMIAccountHolderType | string;
  accountType: keyof typeof NMIAccountType | string;
  errors: InputErrorModel<NMIAccountInfoModel>[] = [];

  constructor(data?: Partial<NMIAccountInfoModel>) {
    this.checkAccount = data?.checkAccount || "";
    this.checkAba = data?.checkAba || "";
    this.accountHolderType = data?.accountHolderType || "";
    this.accountType = data?.accountType || "";

    this.errors =
      data?.errors?.map((e) => new InputErrorModel<NMIAccountInfoModel>(e)) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof NMIAccountInfoModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof NMIAccountInfoModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<NMIAccountInfoModel>[] = [];

    CoreInputModelUtil.validateTextField(
      this.checkAccount,
      "checkAccount",
      newErrors,
      undefined,
      "Please enter check account number",
    );

    NMIProcessorFieldsModel.validateFieldWithRegex<NMIAccountInfoModel>(
      this.checkAba,
      "checkAba",
      newErrors,
      /^[0-9]{9}$/,
      "Check ABA / routing number must be exactly 9 digits",
    );

    CoreInputModelUtil.validateTextField(
      this.accountHolderType,
      "accountHolderType",
      newErrors,
      undefined,
      "Please select account holder type",
    );

    CoreInputModelUtil.validateTextField(
      this.accountType,
      "accountType",
      newErrors,
      undefined,
      "Please select account type",
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;

    return this.errors.length === 0;
  }

  createAPIModel(): INMIAccountInfo {
    return {
      checkAccount: this.checkAccount,
      checkAba: this.checkAba,
      accountHolderType: this.accountHolderType,
      accountType: this.accountType,
    };
  }
}

export class NMIMerchantAccountModel {
  accountInfo: NMIAccountInfoModel;
  type: string;
  company: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  username: string;
  countryDetails: CountryWithStateModel;
  phoneCountryDetails: CountryWithStateModel;
  stateId: string;
  processor: keyof typeof NMIProcessorTypes;
  processorAccountClassification: string;
  maxMonthlyVolume: string;
  duplicateTime: string;
  mcc: string;
  paymentTypes: string[];
  currencies: string[];
  processorFields: NMIProcessorFieldsModel;
  errors: InputErrorModel<NMIMerchantAccountModel>[] = [];
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  constructor(data?: Partial<NMIMerchantAccountModel>) {
    this.type = data?.type || "gateway";
    this.company = data?.company || "";
    this.country = data?.country || "US";
    this.firstName = data?.firstName || "";
    this.lastName = data?.lastName || "";
    this.email = data?.email || "";
    this.phoneCountryDetails =
      data?.phoneCountryDetails || new CountryWithStateModel();
    this.stateId = data?.stateId || "";
    this.phone = data?.phone || "";
    this.username = data?.username || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.accountInfo = new NMIAccountInfoModel(data?.accountInfo);
    this.processor =
      (data?.processor as keyof typeof NMIProcessorTypes) || "Tsys";
    this.processorFields = new NMIProcessorFieldsModel(data?.processorFields);
    this.processorAccountClassification =
      data?.processorAccountClassification || "";
    this.maxMonthlyVolume = data?.maxMonthlyVolume || "";
    //this.duplicateTime = data?.duplicateTime || "";
    this.duplicateTime = "10";
    //this.paymentTypes = data?.paymentTypes || [];
    this.paymentTypes = [
      "visa",
      "mastercard",
      "amex",
      "discover",
      "diners",
      "jcb",
    ];
    // this.currencies = data?.currencies || [];
    this.currencies = ["USD"];
    this.mcc = data?.mcc || "";
    this.errors =
      data?.errors?.map(
        (e) => new InputErrorModel<NMIMerchantAccountModel>(e),
      ) || [];

    this.city = data?.city || "";
    this.address1 = data?.address1 || "";
    this.address2 = data?.address2 || "";
    this.zipCode = data?.zipCode || "";

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof NMIMerchantAccountModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof NMIMerchantAccountModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validateZip = (
    value: string,
    field: keyof NMIMerchantAccountModel,
    newErrors: InputErrorModel<NMIMerchantAccountModel>[],
  ): void => {
    if (!value) {
      newErrors.push(
        new InputErrorModel<NMIMerchantAccountModel>({
          field: field,
          message: "Please enter zip",
        }),
      );
    } else if (!/^\d+$/.test(value)) {
      newErrors.push(
        new InputErrorModel<NMIMerchantAccountModel>({
          field: field,
          message: "Zip code must be a number",
        }),
      );
    } else if (value.length > 5) {
      newErrors.push(
        new InputErrorModel<NMIMerchantAccountModel>({
          field: field,
          message: "maximum length should be 5 digits",
        }),
      );
    }
  };

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<NMIMerchantAccountModel>[] = [];

    CoreInputModelUtil.validateTextField(
      this.company,
      "company",
      newErrors,
      undefined,
      "Please enter company name",
    );

    CoreInputModelUtil.validateTextField(
      this.firstName,
      "firstName",
      newErrors,
      undefined,
      "Please enter first name",
    );

    CoreInputModelUtil.validateTextField(
      this.lastName,
      "lastName",
      newErrors,
      undefined,
      "Please enter last name",
    );

    NMIProcessorFieldsModel.validateFieldWithRegex(
      this.mcc,
      "mcc",
      newErrors,
      /^[0-9]{4}$/,
      "MCC must be exactly 4 numeric digits",
    );

    CoreInputModelUtil.validateEmail("email", this.email, newErrors);

    CoreInputModelUtil.validatePhoneNumber("phone", this.phone, newErrors);

    CoreInputModelUtil.validateTextField(
      this.username,
      "username",
      newErrors,
      undefined,
      "Please enter username",
    );

    CoreInputModelUtil.validateTextField(
      this.processor,
      "processor",
      newErrors,
      undefined,
      "Please select a processor",
    );

    CoreInputModelUtil.validateTextField(
      this.processorAccountClassification,
      "processorAccountClassification",
      newErrors,
      undefined,
      "Please select processor account classification",
    );

    CoreInputModelUtil.validateDecimalValue(
      this.maxMonthlyVolume,
      "maxMonthlyVolume",
      newErrors,
      undefined,
      false,
      "Please enter a valid maximum monthly volume",
    );

    CoreInputModelUtil.validateDecimalValue(
      this.duplicateTime,
      "duplicateTime",
      newErrors,
      undefined,
      true,
      "Please enter duplicate time",
    );

    this.validateZip(this.zipCode, "zipCode", newErrors);

    CoreInputModelUtil.validateTextField(
      this.city,
      "city",
      newErrors,
      undefined,
      "Please enter city",
    );

    CoreInputModelUtil.validateTextField(
      this.countryDetails?.id,
      "countryDetails",
      newErrors,
      undefined,
      "Please select country",
    );

    CoreInputModelUtil.validateTextField(
      this.stateId,
      "stateId",
      newErrors,
      undefined,
      "Please select state",
    );

    CoreInputModelUtil.validateTextField(
      this.address1,
      "address1",
      newErrors,
      undefined,
      "Please enter address1",
    );

    if (this.paymentTypes.length === 0) {
      newErrors.push(
        new InputErrorModel<NMIMerchantAccountModel>({
          field: "paymentTypes",
          message: "Please select at least one payment type",
        }),
      );
    }

    if (this.currencies.length === 0) {
      newErrors.push(
        new InputErrorModel<NMIMerchantAccountModel>({
          field: "currencies",
          message: "Please select at least one currency",
        }),
      );
    }

    const isValidAccountInfo = this.accountInfo.validate(isAutoRun);
    const isValidProcessorFields = this.processorFields.validate(
      isAutoRun,
      this.processor as keyof typeof NMIProcessorTypes,
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return (
      this.errors.length === 0 && isValidAccountInfo && isValidProcessorFields
    );
  }

  createAPIModelForProcessor(): INMIProcessorRequest {
    return {
      processorFields: this.processorFields.createAPIModel(
        this.processor as keyof typeof NMIProcessorTypes,
        this.mcc,
      ),
      processorName: this.processor,
      maxMonthlyVolume: parseFloat(this.maxMonthlyVolume),
      duplicateTime: this.duplicateTime,
      paymentMethodsTypes: this.paymentTypes,
      currencies: this.currencies,
      // MCC is always sent at the top level for every processor.
      mcc: this.mcc,
    };
  }

  createNMIMerchantModel(): IAPICreateNMIMerchantModel {
    const states = this.countryDetails.states;
    const state = states.find((s) => s.id === this.stateId);
    return {
      accountInfo: this.accountInfo.createAPIModel(),
      company: this.company,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      phoneNumber: this.phone,
      userName: this.username,
      phoneCountryCode: this.phoneCountryDetails.code.toString(),
      phoneCountryId: this.phoneCountryDetails.id.toString(),
      countryId: this.countryDetails.id,
      countryCode: this.countryDetails.code.toString(),
      country: this.countryDetails.abbreviationName,
      stateId: this.stateId,
      state: state ? state.abbreviationName : "",
      processorType: this.processor as keyof typeof NMIProcessorTypes,
      processor: this.createAPIModelForProcessor(),
      zipCode: this.zipCode || "",
      address1: this.address1 || "",
      address2: this.address2 || "",
      city: this.city || "",
    };
  }

  createAPIModel(): IAPICreateMerchantAccountModel {
    return {
      accountId: AuthService.authData.accountId,
      source: "NMI",
      fiservApplication: null,
      NMIApplication: this.createNMIMerchantModel(),
      mindmeAccountDetails: AuthService.mindmeAccountDetails,
    };
  }
}
export class NMIProcessorFieldsModel {
  acquirerBIN: string;
  merchantNumber: string;
  storeNumber: string;
  terminalNumber: string;
  merchantCategory: string;
  accountId: string;
  locationNumber: string;
  vitalNumber: string;
  agentBank: string;
  agentChain: string;
  city: string;
  state: string;
  zip: string;
  country: CountryWithStateModel;
  canAcceptCanadianMasterCardDebitCards: boolean;
  bankNumber: string;
  terminalId: string;
  visaMerchantId: string;
  mastercardMerchantId: string;
  processor: keyof typeof NMIProcessorTypes;
  ignoreAmexCVVResult: boolean;
  disablePurchaseReturnAuthorization: boolean;
  // First Data Nashville North V2
  aggregatorMID: boolean;
  // Global Payments East - EMV
  merchantEnrolledForSurcharging: boolean;

  errors: InputErrorModel<NMIProcessorFieldsModel>[] = [];

  constructor(data?: Partial<NMIProcessorFieldsModel>) {
    this.acquirerBIN = data?.acquirerBIN || "";
    this.merchantNumber = data?.merchantNumber || "";
    this.storeNumber = data?.storeNumber || "";
    this.terminalNumber = data?.terminalNumber || "";
    this.merchantCategory = data?.merchantCategory || "";
    this.locationNumber = data?.locationNumber || "";
    this.mastercardMerchantId = data?.mastercardMerchantId || "";
    this.vitalNumber = data?.vitalNumber || "";
    this.agentBank = data?.agentBank || "";
    this.processor = data?.processor || "Tsys";
    this.accountId = data?.accountId || "";
    this.agentChain = data?.agentChain || "";
    this.city = data?.city || "";
    this.state = data?.state || "";
    this.zip = data?.zip || "";
    this.country = data?.country || new CountryWithStateModel();
    this.canAcceptCanadianMasterCardDebitCards =
      data?.canAcceptCanadianMasterCardDebitCards || false;
    this.bankNumber = data?.bankNumber || "";
    this.terminalId = data?.terminalId || "";
    this.visaMerchantId = data?.visaMerchantId || "";
    this.ignoreAmexCVVResult = data?.ignoreAmexCVVResult || false;
    this.disablePurchaseReturnAuthorization =
      data?.disablePurchaseReturnAuthorization || false;
    this.aggregatorMID = data?.aggregatorMID || false;
    // Global Payments East - EMV: surcharging enrollment defaults to true.
    this.merchantEnrolledForSurcharging =
      data?.merchantEnrolledForSurcharging ?? true;

    this.errors =
      data?.errors?.map(
        (e) => new InputErrorModel<NMIProcessorFieldsModel>(e),
      ) || [];

    autorun(() => {
      this.validate(true, this.processor);
    });
  }

  hasError(field: keyof NMIProcessorFieldsModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof NMIProcessorFieldsModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  static validateFieldWithRegex<T>(
    value: string,
    field: keyof T,
    errors: InputErrorModel<T>[],
    regex: RegExp,
    message: string,
    index?: number,
  ): void {
    if (!value || value.trim() === "") {
      errors.push(
        new InputErrorModel<T>({
          field,
          index,
          message: "This field is required",
        }),
      );
    } else if (!regex.test(value)) {
      errors.push(new InputErrorModel<T>({ field, index, message }));
    }
  }

  validate(
    isAutoRun = false,
    processor?: keyof typeof NMIProcessorTypes,
  ): boolean {
    const newErrors: InputErrorModel<NMIProcessorFieldsModel>[] = [];
    switch (processor) {
      case "Tsys":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.acquirerBIN,
          "acquirerBIN",
          newErrors,
          /^[0-9]{6}$/,
          "Acquirer BIN must be exactly 6 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber,
          "merchantNumber",
          newErrors,
          /^[0-9]{12}$/,
          "Merchant Number must be exactly 12 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.storeNumber,
          "storeNumber",
          newErrors,
          /^[0-9]{4}$/,
          "Store Number must be exactly 4 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalNumber,
          "terminalNumber",
          newErrors,
          /^[0-9]{4}$/,
          "Terminal Number must be exactly 4 digits",
        );

        // NMIProcessorFieldsModel.validateFieldWithRegex(
        //   this.merchantCategory,
        //   "merchantCategory",
        //   newErrors,
        //   /^[0-9]{4}$/,
        //   "MCC must be exactly 4 numeric digits"
        // );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.locationNumber,
          "locationNumber",
          newErrors,
          /^[0-9]{5}$/,
          "Location Number must be 5 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.vitalNumber,
          "vitalNumber",
          newErrors,
          /^[0-9]{8}$/,
          "Vital Number must be 8 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.agentBank,
          "agentBank",
          newErrors,
          /^[0-9]{6}$/,
          "Agent Bank must be 6 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.agentChain,
          "agentChain",
          newErrors,
          /^[0-9]{6}$/,
          "Agent Chain must be 6 digits",
        );

        if (this.city) {
          CoreInputModelUtil.validateTextField(
            this.city,
            "city",
            newErrors,
            undefined,
            "Please enter a valid city",
          );
        }

        if (this.state) {
          CoreInputModelUtil.validateTextField(
            this.state,
            "state",
            newErrors,
            undefined,
            "Please select a state",
          );
        }

        if (this.zip) {
          NMIProcessorFieldsModel.validateFieldWithRegex(
            this.zip,
            "zip",
            newErrors,
            /^[0-9]{5}$/,
            "ZIP must be 5 digits",
          );
        }
        break;
      case "ElavonEMV":
        // Fields + regexes are driven by ELAVON_EMV_CONFIG (serviceConfig doc).
        // Bank Number (id 1): required, exactly 6 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.bankNumber.trim(),
          "bankNumber",
          newErrors,
          getFieldRegExp(ELAVON_EMV_CONFIG, "1") ?? /^[0-9]{6}$/,
          "Bank Number must be exactly 6 digits",
        );
        // Terminal ID (id 2): required, up to 16 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId.trim(),
          "terminalId",
          newErrors,
          getFieldRegExp(ELAVON_EMV_CONFIG, "2") ?? /^[0-9]{0,16}$/,
          "Terminal ID must be up to 16 digits",
        );
        // Merchant ID (id 3): required, up to 16 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.visaMerchantId.trim(),
          "visaMerchantId",
          newErrors,
          getFieldRegExp(ELAVON_EMV_CONFIG, "3") ?? /^[0-9]{0,16}$/,
          "Merchant ID must be up to 16 digits",
        );
        break;
      case "ElavonViaConex":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.bankNumber,
          "bankNumber",
          newErrors,
          /^[0-9]{6}$/,
          "Bank Number must be exactly 6 digits",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId,
          "terminalId",
          newErrors,
          /^[0-9]{6}$/,
          "Terminal ID must be exactly 6 digits",
        );

        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.visaMerchantId,
          "visaMerchantId",
          newErrors,
          /^[0-9]{13}$/,
          "Merchant ID must be exactly 13 digits",
        );
        break;
      case "FirstDataNashville":
        // Fields + regexes are driven by FIRST_DATA_NASHVILLE_CONFIG.
        // Merchant ID (id 1): required, digits only.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber.trim(),
          "merchantNumber",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_CONFIG, "1") ?? /^[0-9]*$/,
          "Merchant ID must contain digits only",
        );
        // Terminal ID (id 2): required, digits only (up to 11 digits).
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId.trim(),
          "terminalId",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_CONFIG, "2") ?? /^[0-9]*$/,
          "Terminal ID must contain digits only (up to 11 digits)",
        );
        break;
      case "GlobalCollect":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.accountId,
          "accountId",
          newErrors,
          /^[0-9]{1,12}$/,
          "Account ID is required",
        );
        break;
      case "FirstDataNashvilleNorth":
        // Required fields + regexes are driven by
        // FIRST_DATA_NASHVILLE_NORTH_CONFIG. Optional fields (MVV, Backend
        // MID, Visa Debt Repayment) are not collected on this form.
        // Merchant ID (id 1): required, exactly 12 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber.trim(),
          "merchantNumber",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_NORTH_CONFIG, "1") ??
            /^[0-9]{12}$/,
          "Merchant ID must be exactly 12 digits",
        );
        // Terminal ID (id 3): required, 1-8 alphanumeric characters.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId.trim(),
          "terminalId",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_NORTH_CONFIG, "3") ??
            /^[0-9a-zA-Z]{1,8}$/,
          "Terminal ID must be 1-8 characters (letters or numbers)",
        );
        // Merchant Category Code (id 4) is sourced from the shared MCC field
        // in the Processor section, so it is not collected/validated here.
        break;
      case "FirstDataNashvilleNorthV2":
        // Required fields + regexes are driven by
        // FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG. MCC (id 4) is collected via the
        // shared MCC field (validated on NMIMerchantAccountModel). Optional
        // fields (AMEX Restaurant ID, MVV, Backend MID, Visa Debt Repayment)
        // are not collected on this form.
        // Merchant ID (id 1): required, exactly 12 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber.trim(),
          "merchantNumber",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG, "1") ??
            /^[0-9]{12}$/,
          "Merchant ID must be exactly 12 digits",
        );
        // Terminal ID (id 3): required, 1-8 alphanumeric characters.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId.trim(),
          "terminalId",
          newErrors,
          getFieldRegExp(FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG, "3") ??
            /^[0-9a-zA-Z]{1,8}$/,
          "Terminal ID must be 1-8 characters (letters or numbers)",
        );
        break;
      case "FirstDataRapidConnectNashvilleNorthEMV":
      case "FirstDataRapidConnectCardnetNorthEMV":
        // North back-end: 7 digit MID, 7 digit Bank TID, 12 digit North Settlement MID
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber,
          "merchantNumber",
          newErrors,
          /^[0-9]{7}$/,
          "Merchant Number must be exactly 7 digits",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId,
          "terminalId",
          newErrors,
          /^[0-9]{7}$/,
          "Terminal ID must be exactly 7 digits",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.visaMerchantId,
          "visaMerchantId",
          newErrors,
          /^[0-9]{12}$/,
          "Settlement Merchant ID must be exactly 12 digits",
        );
        break;
      case "GlobalPaymentsEastEMV":
        // Required fields + regexes are driven by GLOBAL_PAYMENTS_EAST_EMV_CONFIG.
        // Merchant ID (id 1): required, digits only.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.visaMerchantId.trim(),
          "visaMerchantId",
          newErrors,
          getFieldRegExp(GLOBAL_PAYMENTS_EAST_EMV_CONFIG, "1") ?? /^[0-9]+$/,
          "Merchant ID must contain digits only",
        );
        // Bank ID (id 2): required, exactly 6 digits.
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.bankNumber.trim(),
          "bankNumber",
          newErrors,
          getFieldRegExp(GLOBAL_PAYMENTS_EAST_EMV_CONFIG, "2") ?? /^[0-9]{6}$/,
          "Bank ID must be exactly 6 digits",
        );
        break;
      case "FirstDataCanada":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.visaMerchantId,
          "visaMerchantId",
          newErrors,
          /^[0-9]{11}$/,
          "Merchant ID must be exactly 11 digits",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.mastercardMerchantId,
          "mastercardMerchantId",
          newErrors,
          /^[0-9]{11}$/,
          "Merchant ID must be exactly 11 digits",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId,
          "terminalId",
          newErrors,
          /^[0-9]{1,8}$/,
          "Terminal ID must be upto 8 characters",
        );
        // NMIProcessorFieldsModel.validateFieldWithRegex(
        //   this.merchantCategory,
        //   "merchantCategory",
        //   newErrors,
        //   /^[0-9]{4}$/,
        //   "MCC must be exactly 4 numeric digits"
        // );
        break;
      case "FirstDataOmaha":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber,
          "merchantNumber",
          newErrors,
          /^[0-9]+$/,
          "Merchant Number must be a positive integer",
        );
        break;
      case "FirstDataRapidConnectOmahaEMV":
      case "FirstDataRapidConnectNashvilleEMV":
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.merchantNumber,
          "merchantNumber",
          newErrors,
          /^[0-9A-Za-z]{0,16}$/,
          "Merchant Number must be upto 16 characters",
        );
        NMIProcessorFieldsModel.validateFieldWithRegex(
          this.terminalId,
          "terminalId",
          newErrors,
          /^[0-9A-Za-z]{0,8}$/,
          "Terminal ID must be upto 8 characters",
        );
        break;
    }

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createAPIModel(
    processor: keyof typeof NMIProcessorTypes,
    mcc: string,
  ): INMIProcessorFieldsRequest[] {
    switch (processor) {
      case "Tsys":
        return [
          {
            fieldId: 1,
            fieldName: "Merchant Number",
            fieldValue: this.merchantNumber,
          },
          {
            fieldId: 2,
            fieldName: "Acquirer BIN",
            fieldValue: this.acquirerBIN,
          },
          {
            fieldId: 3,
            fieldName: "Store Number",
            fieldValue: this.storeNumber,
          },
          {
            fieldId: 4,
            fieldName: "Terminal Number",
            fieldValue: this.terminalNumber,
          },
          {
            fieldId: 5,
            fieldName: "Merchant Category",
            fieldValue: mcc,
          },
          {
            fieldId: 6,
            fieldName: "Location Number",
            fieldValue: this.locationNumber,
          },
          {
            fieldId: 7,
            fieldName: "VITAL Number",
            fieldValue: this.vitalNumber,
          },
          { fieldId: 8, fieldName: "Agent Bank", fieldValue: this.agentBank },
          { fieldId: 9, fieldName: "Agent Chain", fieldValue: this.agentChain },
          {
            fieldId: 10,
            fieldName: "Country",
            fieldValue: this.country.abbreviationName || this.country.name,
          },
          { fieldId: 11, fieldName: "City", fieldValue: this.city },
          { fieldId: 12, fieldName: "State", fieldValue: this.state },
          { fieldId: 13, fieldName: "ZIP", fieldValue: this.zip },
          {
            fieldId: 16,
            fieldName: "Time Zone Differential",
            fieldValue: "705",
          },
          {
            fieldId: 19,
            fieldName: "Merchant can accept Canadian MasterCard Debit Cards",
            fieldValue: this.canAcceptCanadianMasterCardDebitCards
              ? "true"
              : "false",
          },
        ];
      case "ElavonEMV":
        // Field ids/names per ELAVON_EMV_CONFIG serviceConfig.
        return [
          {
            fieldId: 1,
            fieldName: "Bank Number",
            fieldValue: this.bankNumber.trim(),
          },
          {
            fieldId: 2,
            fieldName: "Terminal ID",
            fieldValue: this.terminalId.trim(),
          },
          {
            fieldId: 3,
            fieldName: "Merchant ID",
            fieldValue: this.visaMerchantId.trim(),
          },
        ];
      case "ElavonViaConex":
        return [
          { fieldId: 1, fieldName: "Bank Number", fieldValue: this.bankNumber },
          { fieldId: 2, fieldName: "Terminal ID", fieldValue: this.terminalId },
          {
            fieldId: 3,
            fieldName: "Merchant ID",
            fieldValue: this.visaMerchantId,
          },
        ];
      case "FirstDataNashville":
        // Field ids/names per FIRST_DATA_NASHVILLE_CONFIG serviceConfig.
        return [
          {
            fieldId: 1,
            fieldName: "Merchant ID",
            fieldValue: this.merchantNumber.trim(),
          },
          {
            fieldId: 2,
            fieldName: "Terminal ID",
            fieldValue: this.terminalId.trim(),
          },
          {
            fieldId: 3,
            fieldName: "Ignore Amex CVV Results",
            fieldValue: this.ignoreAmexCVVResult ? "true" : "false",
          },
          {
            fieldId: 14,
            fieldName:
              "Disable Purchase Return Authorization functionality for refunds and credits",
            fieldValue: this.disablePurchaseReturnAuthorization
              ? "true"
              : "false",
          },
        ];
      case "FirstDataNashvilleNorth":
        // Field ids/names per FIRST_DATA_NASHVILLE_NORTH_CONFIG serviceConfig.
        // Only required fields are collected; optional fields are omitted.
        return [
          {
            fieldId: 1,
            fieldName: "Merchant ID",
            fieldValue: this.merchantNumber.trim(),
          },
          {
            fieldId: 3,
            fieldName: "Terminal ID",
            fieldValue: this.terminalId.trim(),
          },
          {
            fieldId: 4,
            fieldName: "Merchant Category Code",
            fieldValue: mcc,
          },
          {
            fieldId: 7,
            fieldName:
              "Disable Purchase Return Authorization functionality for refunds and credits",
            fieldValue: this.disablePurchaseReturnAuthorization
              ? "true"
              : "false",
          },
        ];
      case "FirstDataNashvilleNorthV2":
        // Field ids/names per FIRST_DATA_NASHVILLE_NORTH_V2_CONFIG serviceConfig.
        // MCC (id 4) is sourced from the shared MCC field. Only required fields
        // are collected; optional fields are omitted.
        return [
          {
            fieldId: 1,
            fieldName: "Merchant ID",
            fieldValue: this.merchantNumber.trim(),
          },
          {
            fieldId: 3,
            fieldName: "Terminal ID",
            fieldValue: this.terminalId.trim(),
          },
          {
            fieldId: 4,
            fieldName: "Merchant Category Code",
            fieldValue: mcc,
          },
          {
            fieldId: 6,
            fieldName: "Aggregator MID",
            fieldValue: this.aggregatorMID ? "true" : "false",
          },
          {
            fieldId: 7,
            fieldName:
              "Disable Purchase Return Authorization functionality for refunds and credits",
            fieldValue: this.disablePurchaseReturnAuthorization
              ? "true"
              : "false",
          },
        ];
      case "FirstDataRapidConnectNashvilleNorthEMV":
      case "FirstDataRapidConnectCardnetNorthEMV":
        return [
          {
            fieldId: 1,
            fieldName: "Rapid Connect Merchant Number",
            fieldValue: this.merchantNumber,
          },
          {
            fieldId: 2,
            fieldName: "Rapid Connect Terminal ID",
            fieldValue: this.terminalId,
          },
          {
            fieldId: 3,
            fieldName: "Settlement Merchant ID",
            fieldValue: this.visaMerchantId,
          },
        ];
      case "GlobalPaymentsEastEMV":
        // Field ids/names per GLOBAL_PAYMENTS_EAST_EMV_CONFIG serviceConfig.
        // Only required fields are collected (Disable Purchase Return Auth is
        // optional and omitted).
        return [
          {
            fieldId: 1,
            fieldName: "Merchant ID",
            fieldValue: this.visaMerchantId.trim(),
          },
          {
            fieldId: 2,
            fieldName: "Bank ID",
            fieldValue: this.bankNumber.trim(),
          },
          {
            fieldId: 4,
            fieldName: "Merchant is enrolled for surcharging",
            fieldValue: this.merchantEnrolledForSurcharging ? "true" : "false",
          },
        ];
      case "GlobalCollect":
        return [
          { fieldId: 1, fieldName: "Account ID", fieldValue: this.accountId },
        ];
      case "FirstDataCanada":
        return [
          {
            fieldId: 1,
            fieldName: "Visa Merchant ID",
            fieldValue: this.visaMerchantId,
          },
          {
            fieldId: 2,
            fieldName: "Merchant ID",
            fieldValue: this.visaMerchantId,
          },
          { fieldId: 3, fieldName: "Terminal ID", fieldValue: this.terminalId },
          {
            fieldId: 4,
            fieldName: "Merchant Category",
            fieldValue: mcc,
          },
        ];
      case "FirstDataOmaha":
        return [
          {
            fieldId: 1,
            fieldName: "Merchant Number",
            fieldValue: this.merchantNumber,
          },
        ];
      case "FirstDataRapidConnectNashvilleEMV":
      case "FirstDataRapidConnectOmahaEMV":
        return [
          {
            fieldId: 1,
            fieldName: "Rapid Connect Merchant Number",
            fieldValue: this.merchantNumber,
          },
          {
            fieldId: 2,
            fieldName: "Rapid Connect Terminal ID",
            fieldValue: this.terminalId,
          },
        ];
      default:
        return [];
    }
  }
}
